"use client";

import Link from "next/link";

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  /**
   * If true, render an explicit "← Back to {parent}" pill before the trail.
   * Defaults to true. The parent is the last item that has an href.
   */
  showBackLink?: boolean;
}

/**
 * Site-wide breadcrumb. Render above page <h1> on any sub-page that is reached
 * via a QuickLink, drill-down, or hamburger nav — anywhere the user might
 * otherwise feel "stuck" with no obvious way back.
 *
 * Usage:
 *   <Breadcrumb items={[
 *     { label: "Settings", href: "/dashboard/settings" },
 *     { label: "Admin" },
 *   ]} />
 */
export function Breadcrumb({ items, showBackLink = true }: BreadcrumbProps) {
  if (items.length === 0) return null;

  // Find the last item that has an href — that's the "parent" we can go back to.
  const parent = [...items].reverse().find((i) => i.href);

  return (
    <nav aria-label="Breadcrumb" className="mb-4 flex flex-wrap items-center gap-2">
      {showBackLink && parent && (
        <Link
          href={parent.href!}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs font-medium text-text-muted transition hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          <span>Back to {parent.label}</span>
        </Link>
      )}

      <ol className="flex flex-wrap items-center gap-1.5 text-xs text-text-dim">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-1.5">
              {index > 0 && (
                <svg className="h-3 w-3 text-text-dim" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              )}
              {item.href && !isLast ? (
                <Link
                  href={item.href}
                  className="rounded-md px-1.5 py-0.5 text-text-muted transition hover:bg-white/5 hover:text-white"
                >
                  {item.label}
                </Link>
              ) : (
                <span className={isLast ? "font-medium text-white/80" : "text-text-muted"}>
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
